<template>
  <div id="permissions-panel">
    <header class="row mb-2">
      <div class="col-sm-8">
        <slot>
          <h5>{{ $t("permissionPanel.permissionsManagement") }}</h5>
        </slot>
      </div>
      <div class="text-sm-right col-sm-4">
        <BButton :disabled="!isDirty" class="pull-right" size="sm" variant="light" @click="onReset">
          <font-awesome-icon icon="times"></font-awesome-icon>
        </BButton>
        <BButton
          :disabled="!isDirty"
          class="pull-right ml-1"
          size="sm"
          variant="primary"
          @click="onSave"
          style="background-color: #0054a6"
        >
          <font-awesome-icon icon="save"></font-awesome-icon>
        </BButton>
      </div>
    </header>

    <BOverlay :show="formLoading" rounded="sm" variant="white" opacity="0.6" blur="2px" v-if="!disabled">
      <BCard
        :title="group.displayName ?? undefined"
        v-for="group in groups"
        :key="group.name ?? undefined"
        class="mb-1"
      >
        <BCardBody>
          <BFormGroup
            v-for="permission in group.permissions"
            :key="permission.name ?? undefined"
            :class="permission.parentName != null ? 'ml-4' : ''"
          >
            <BFormCheckbox v-model="permission.isGranted" size="md" switch>
              {{ permission.displayName }}
            </BFormCheckbox>
          </BFormGroup>
        </BCardBody>
      </BCard>
    </BOverlay>
    <p v-else class="text-center mt-4">{{ $t("permissionPanel.nothingToShow") }}</p>
  </div>
</template>

<script lang="ts">
import { GetPermissionListResultDto, PermissionGroupDto, UpdatePermissionsDto } from "@/api/model";
import { PermissionsApi } from "@/api";
import isEqual from "lodash/isEqual";
import { defineComponent } from "vue";
import { useToast } from "vue-toastification";
import { BCard, BCardBody, BFormCheckbox, BFormGroup, BOverlay, BButton } from "bootstrap-vue-next";

export default defineComponent({
  name: "PermissionsPanel",
  components: {
    BCard,
    BCardBody,
    BOverlay,
    BFormCheckbox,
    BFormGroup,
    BButton
  },
  props: {
    disabled: {
      type: Boolean,
      required: true
    },
    providerName: {
      type: String,
      required: true
    },
    providerKey: {
      type: String
    }
  },
  data() {
    return {
      savedPermissions: null as GetPermissionListResultDto | null,
      permissions: null as GetPermissionListResultDto | null,
      formLoading: false,
      toast: null as any
    };
  },
  setup() {
    const permissionsApi = new PermissionsApi()!;
    return {
      permissionsApi
    };
  },
  computed: {
    groups(): PermissionGroupDto[] {
      return this.permissions?.groups ?? [];
    },
    isDirty(): boolean {
      return !isEqual(this.permissions, this.savedPermissions);
    }
  },
  async mounted(): Promise<void> {
    if (!this.disabled) {
      await this.readPermissions();
    }
  },
  methods: {
    async readPermissions(): Promise<void> {
      this.formLoading = true;
      const data = await this.permissionsApi.apiPermissionManagementPermissionsGet(this.providerName, this.providerKey);
      this.permissions = data.data;
      this.savedPermissions = structuredClone(data.data);
      this.formLoading = false;
    },

    async onSave(): Promise<void> {
      this.formLoading = true;

      const updatePermissionsDto: UpdatePermissionsDto = {
        permissions: []
      };

      this.permissions?.groups?.forEach(group => {
        group.permissions?.forEach(permission => {
          updatePermissionsDto.permissions?.push({
            name: permission.name,
            isGranted: permission.isGranted
          });
        });
      });

      await this.permissionsApi
        .apiPermissionManagementPermissionsPut(this.providerName, this.providerKey, updatePermissionsDto)
        .then(async () => {
          this.toast.success("Permissions Updated", {
            toastClassName: "custom-success-toast",
            timeout: 5000
          });
          await this.readPermissions();
        })
        .catch(e => {
          this.toast.error(e.errorMsg, {
            toastClassName: "custom-error-toast",
            timeout: 5000
          });
        })
        .finally(() => {
          this.formLoading = false;
        });
    },

    onReset(): void {
      this.permissions = structuredClone(this.savedPermissions);
    }
  },
  created() {
    this.toast = useToast();
  }
});
</script>
<style>
.form-switch .form-check-input:checked {
  background-color: #0054a6 !important;
}
</style>
